// src/services/api/index.js - Complete Enhanced API service with optimizations and security fixes
import axios from "axios";

const API_URL =
  process.env.REACT_APP_API_URL ||
  (process.env.NODE_ENV === "production"
    ? "/api/v1"
    : "http://localhost:8000/api/v1");


// Secure storage utility
class SecureStorage {
  static setItem(key, value) {
    try {
      // Use sessionStorage for sensitive data instead of localStorage
      const data = typeof value === "string" ? value : JSON.stringify(value);
      sessionStorage.setItem(key, btoa(data)); // Basic encoding
    } catch (error) {
      console.warn("Failed to store data securely:", error);
    }
  }

  static getItem(key) {
    try {
      const data = sessionStorage.getItem(key);
      return data ? atob(data) : null;
    } catch (error) {
      console.warn("Failed to retrieve data securely:", error);
      return null;
    }
  }

  static removeItem(key) {
    sessionStorage.removeItem(key);
  }
}

// Improved Circuit Breaker with better thresholds
class CircuitBreaker {
  constructor(threshold = 10, timeout = 120000, monitoringPeriod = 300000) { // Increased thresholds
    this.failureCount = 0;
    this.successCount = 0;
    this.threshold = threshold; // Increased from 5 to 10
    this.timeout = timeout; // Increased from 60s to 120s
    this.monitoringPeriod = monitoringPeriod; // Increased to 5 minutes
    this.state = "CLOSED";
    this.nextAttempt = Date.now();
    this.lastFailureTime = null;
    this.consecutiveFailures = 0; // Add consecutive failure tracking

    // Auto-reset on success
    setInterval(() => {
      if (this.state === "CLOSED" && this.failureCount > 0) {
        this.failureCount = Math.max(0, this.failureCount - 2); // Faster recovery
        this.consecutiveFailures = Math.max(0, this.consecutiveFailures - 1);
      }
    }, this.monitoringPeriod);
  }

  async call(fn) {
    // Always allow if circuit is closed or if enough time has passed
    if (this.state === "OPEN") {
      if (Date.now() < this.nextAttempt) {
        // For patient creation, allow bypass after 30 seconds instead of full timeout
        const timeSinceLastFailure = Date.now() - (this.lastFailureTime || 0);
        if (timeSinceLastFailure > 30000) { // 30 seconds grace period
          console.log("Circuit breaker: Allowing request after grace period");
          this.state = "HALF_OPEN";
        } else {
          throw new Error("Service temporarily unavailable. Please wait a moment and try again.");
        }
      } else {
        this.state = "HALF_OPEN";
      }
    }

    try {
      const result = await fn();
      this.onSuccess();
      return result;
    } catch (error) {
      // Don't count validation errors as circuit breaker failures
      if (error.response?.status === 422 || error.response?.status === 400) {
        throw error; // Pass through validation errors without triggering circuit breaker
      }
      this.onFailure();
      throw error;
    }
  }

  onSuccess() {
    this.failureCount = 0;
    this.consecutiveFailures = 0;
    this.successCount++;
    if (this.state === "HALF_OPEN") {
      this.state = "CLOSED";
      console.log("Circuit breaker: Reset to CLOSED after successful request");
    }
  }

  onFailure() {
    this.failureCount++;
    this.consecutiveFailures++;
    this.lastFailureTime = Date.now();
    
    // Only open circuit on consecutive failures to avoid false positives
    if (this.consecutiveFailures >= this.threshold) {
      this.state = "OPEN";
      this.nextAttempt = Date.now() + this.timeout;
      console.warn(`Circuit breaker: OPENED after ${this.consecutiveFailures} consecutive failures`);
    }
  }

  // Add manual reset method
  reset() {
    this.state = "CLOSED";
    this.failureCount = 0;
    this.consecutiveFailures = 0;
    this.successCount = 0;
    console.log("Circuit breaker: Manually reset");
  }
}

class APIService {
  constructor() {
    this.baseURL = API_URL;
    this.timeout = 30000;

    // Enhanced cache system with tiered strategies
    this.cache = new Map();
    this.cacheTimestamps = new Map();
    this.cachePriorities = new Map();
    this.MAX_CACHE_SIZE = 200; // Increased but still bounded
    this.MAX_MEMORY_MB = 50; // Memory limit in MB

    // Cache strategies for different endpoints
    this.CACHE_STRATEGIES = {
      "GET_/users/me": { ttl: 30000, priority: "high" },
      "GET_/facilities": { ttl: 300000, priority: "medium" },
      "GET_/medical-professionals": { ttl: 180000, priority: "medium" },
      "GET_/analytics": { ttl: 60000, priority: "low" },
      "GET_/orders": { ttl: 10000, priority: "high" },
      "GET_/patients": { ttl: 30000, priority: "medium" },
      "GET_/security-status": { ttl: 30000, priority: "high" },
      "GET_/health": { ttl: 5000, priority: "low" },
      default: { ttl: 60000, priority: "medium" },
    };

    // Request deduplication with improved key generation
    this.pendingRequests = new Map();

    // Request queue for rate limiting
    this.requestQueue = [];
    this.isProcessingQueue = false;
    this.maxConcurrentRequests = 5;
    this.activeRequests = 0;

    // Circuit breaker for network resilience
    this.circuitBreaker = new CircuitBreaker();

    // Retry configuration
    this.maxRetries = 3;
    this.retryDelay = 1000;

    this.setupAxios();
    this.startCacheCleanup();
    this.startMemoryMonitoring();
  }

  async refreshTokenIfNeeded() {
    const token = SecureStorage.getItem("token");
    if (!token) return false;

    try {
      // Check if token is about to expire (decode JWT payload)
      const payload = JSON.parse(atob(token.split(".")[1]));
      const expirationTime = payload.exp * 1000; // Convert to milliseconds
      const currentTime = Date.now();
      const timeUntilExpiry = expirationTime - currentTime;

      // If token expires in less than 5 minutes, refresh it
      if (timeUntilExpiry < 5 * 60 * 1000) {
        return await this.refreshToken();
      }
      return true;
    } catch (error) {
      console.warn("Could not check token expiration:", error);
      return false;
    }
  }

  setupAxios() {
    this.api = axios.create({
      baseURL: this.baseURL,
      timeout: this.timeout,
      headers: {
        "Content-Type": "application/json",
      },
    });

    // Enhanced request interceptor
    this.api.interceptors.request.use(
      async (config) => {
        await this.refreshTokenIfNeeded();

        // Get token from secure storage
        const token = SecureStorage.getItem("token");
        if (token) {
          config.headers["Authorization"] = `Bearer ${token}`;
        }

        // Add CSRF protection
        const csrfToken = document.querySelector(
          'meta[name="csrf-token"]'
        )?.content;
        if (csrfToken) {
          config.headers["X-CSRF-Token"] = csrfToken;
        }

        // Add request ID for tracking
        config.headers["X-Request-ID"] = this.generateRequestId();

        // Avoid setting incorrect Content-Type for FormData
        if (!(config.data instanceof FormData)) {
          config.headers["Content-Type"] =
            config.headers["Content-Type"] || "application/json";
        }

        // Add request tracking
        config.startTime = Date.now();
        config.retryCount = config.retryCount || 0;
        this.activeRequests++;

        // Enhanced logging in development
        if (process.env.NODE_ENV === "development") {
        }

        return config;
      },
      (error) => {
        console.error("❌ Request interceptor error:", error);
        return Promise.reject(error);
      }
    );

    // Enhanced response interceptor with circuit breaker and retry logic
    this.api.interceptors.response.use(
      (response) => {
        const duration = Date.now() - response.config.startTime;
        this.activeRequests = Math.max(0, this.activeRequests - 1);

        // Log successful responses in development
        if (process.env.NODE_ENV === "development") {
        }

        return response;
      },
      async (error) => {
        const originalRequest = error.config;
        this.activeRequests = Math.max(0, this.activeRequests - 1);

        // Enhanced error logging
        if (process.env.NODE_ENV === "development") {
          console.error("❌ API Error:", {
            url: originalRequest?.url,
            method: originalRequest?.method,
            status: error.response?.status,
            data: error.response?.data,
            message: error.message,
            activeRequests: this.activeRequests,
            retryCount: originalRequest?.retryCount || 0,
          });
        }

        // Handle network errors with circuit breaker
        if (!error.response) {
          console.error(
            "🌐 Network error - server may be down:",
            error.message
          );
          const networkError =
            new Error(`Cannot connect to server at ${this.baseURL}. Please check:
1. Backend server is running
2. Server is accessible at the configured URL
3. No firewall is blocking the connection
4. CORS is properly configured`);
          networkError.isNetworkError = true;
          networkError.originalError = error;
          return Promise.reject(networkError);
        }

        // Enhanced retry logic with exponential backoff
        const shouldRetry = this.shouldRetryRequest(error, originalRequest);
        if (shouldRetry) {
          return this.retryRequest(originalRequest);
        }

        // Handle rate limiting with improved retry
        if (error.response?.status === 429) {
          const retryAfter =
            parseInt(error.response.headers["retry-after"]) || 1;
          console.warn(
            `⏳ Rate limited. Retrying after ${retryAfter} seconds...`
          );

          const retryCount = originalRequest.retryCount || 0;
          if (retryCount < this.maxRetries) {
            originalRequest.retryCount = retryCount + 1;
            await this.delay(retryAfter * 1000);
            return this.api(originalRequest);
          } else {
            const rateLimitError = new Error(
              `Rate limit exceeded after ${this.maxRetries} retries. Please wait ${retryAfter} seconds.`
            );
            rateLimitError.isRateLimitError = true;
            rateLimitError.retryAfter = retryAfter;
            return Promise.reject(rateLimitError);
          }
        }

        // Handle 2FA requirement
        if (
          error.response?.status === 206 &&
          error.response?.headers["x-2fa-required"] === "true"
        ) {
          SecureStorage.setItem(
            "pending_2fa",
            JSON.stringify({
              username: originalRequest.data?.username,
              requires_sms: true,
            })
          );

          window.dispatchEvent(
            new CustomEvent("require2FA", {
              detail: error.response.data,
            })
          );

          return Promise.reject(error);
        }

        // Handle 401 Unauthorized globally with improved logic
        if (error.response?.status === 401 && !originalRequest._retry) {
          originalRequest._retry = true;


          // Try to refresh token
          try {
            const refreshed = await this.refreshToken();
            if (refreshed) {
              // Update the original request with new token
              const newToken = SecureStorage.getItem("token");
              originalRequest.headers["Authorization"] = `Bearer ${newToken}`;
              return this.api(originalRequest);
            }
          } catch (refreshError) {
            console.error("🔐 Token refresh failed:", refreshError);
          }

          // If refresh fails, clear auth data and redirect
          this.clearAuthData();
          if (!window.location.pathname.includes("/login")) {
            window.location.href = "/login";
          }
          return Promise.reject(error);
        }

        // Handle 403 VPN required
        if (
          error.response?.status === 403 &&
          error.response?.data?.detail?.includes("VPN")
        ) {
          window.dispatchEvent(
            new CustomEvent("requireVPN", {
              detail: error.response.data,
            })
          );
          return Promise.reject(error);
        }

        // Handle 404 Not Found with fallbacks
        if (error.response?.status === 404) {
          const fallbackResponse = this.getFallbackResponse(
            originalRequest.url
          );
          if (fallbackResponse) {
            console.warn(`📋 Using fallback for ${originalRequest.url}`);
            return Promise.resolve(fallbackResponse);
          }
        }

        // Handle server errors
        if (error.response?.status >= 500) {
          console.error(
            "🚨 Server error:",
            error.response.status,
            error.response.data
          );
          const serverError = new Error(
            `Server error (${error.response.status}): ${
              error.response.data?.detail || "Internal server error"
            }`
          );
          serverError.isServerError = true;
          serverError.originalError = error;
          return Promise.reject(serverError);
        }

        // Handle timeout errors
        if (error.code === "ECONNABORTED") {
          console.error("⏰ Request timeout");
          const timeoutError = new Error(
            "Request timed out. Please check your connection and try again."
          );
          timeoutError.isTimeoutError = true;
          timeoutError.originalError = error;
          return Promise.reject(timeoutError);
        }

        return Promise.reject(error);
      }
    );
  }

  // Enhanced cache management
  getCachedResponse(key) {
    const cached = this.cache.get(key);
    const timestamp = this.cacheTimestamps.get(key);
    const strategy = this.getCacheStrategy(key);

    if (cached && timestamp && Date.now() - timestamp < strategy.ttl) {
      // Update access time for LRU
      this.cacheTimestamps.set(key, Date.now());
      return cached;
    }

    // Clear expired cache
    this.cache.delete(key);
    this.cacheTimestamps.delete(key);
    this.cachePriorities.delete(key);
    return null;
  }

  setCachedResponse(key, response) {
    const strategy = this.getCacheStrategy(key);
    const responseSize = this.estimateSize(response);

    // Check memory limits
    if (
      this.getMemoryUsage() + responseSize >
      this.MAX_MEMORY_MB * 1024 * 1024
    ) {
      this.evictLowPriorityCache();
    }

    // Prevent cache from growing too large
    if (this.cache.size >= this.MAX_CACHE_SIZE) {
      this.evictOldestCache();
    }

    this.cache.set(key, response);
    this.cacheTimestamps.set(key, Date.now());
    this.cachePriorities.set(key, strategy.priority);
  }

  getCacheStrategy(key) {
    // Extract method and path from key
    const [method, path] = key.split("_", 2);
    const strategyKey = `${method}_${path.split("?")[0]}`;

    return this.CACHE_STRATEGIES[strategyKey] || this.CACHE_STRATEGIES.default;
  }

  // Memory management
  getMemoryUsage() {
    let totalSize = 0;
    for (const [key, value] of this.cache.entries()) {
      totalSize += this.estimateSize(value);
    }
    return totalSize;
  }

  estimateSize(obj) {
    return new Blob([JSON.stringify(obj)]).size;
  }

  evictLowPriorityCache() {
    const priorities = { low: 1, medium: 2, high: 3 };
    const entries = Array.from(this.cache.keys()).map((key) => ({
      key,
      priority: priorities[this.cachePriorities.get(key)] || 2,
      timestamp: this.cacheTimestamps.get(key),
    }));

    // Sort by priority (ascending) then by age (ascending)
    entries.sort((a, b) => {
      if (a.priority !== b.priority) return a.priority - b.priority;
      return a.timestamp - b.timestamp;
    });

    // Remove bottom 25%
    const toRemove = entries.slice(0, Math.ceil(entries.length * 0.25));
    toRemove.forEach(({ key }) => {
      this.cache.delete(key);
      this.cacheTimestamps.delete(key);
      this.cachePriorities.delete(key);
    });
  }

  evictOldestCache() {
    const oldestKey = Array.from(this.cacheTimestamps.entries()).sort(
      ([, a], [, b]) => a - b
    )[0]?.[0];

    if (oldestKey) {
      this.cache.delete(oldestKey);
      this.cacheTimestamps.delete(oldestKey);
      this.cachePriorities.delete(oldestKey);
    }
  }

  // Automatic cache cleanup
  startCacheCleanup() {
    setInterval(() => {
      this.cleanupExpiredCache();
    }, 60000); // Clean every minute
  }

  cleanupExpiredCache() {
    const now = Date.now();
    const keysToDelete = [];

    for (const [key, timestamp] of this.cacheTimestamps.entries()) {
      const strategy = this.getCacheStrategy(key);
      if (now - timestamp > strategy.ttl) {
        keysToDelete.push(key);
      }
    }

    keysToDelete.forEach((key) => {
      this.cache.delete(key);
      this.cacheTimestamps.delete(key);
      this.cachePriorities.delete(key);
    });

    if (keysToDelete.length > 0) {
      console.log(`🧹 Cleaned up ${keysToDelete.length} expired cache entries`);
    }
  }

  // Memory monitoring
  startMemoryMonitoring() {
    setInterval(() => {
      const memoryUsage = this.getMemoryUsage();
      const memoryMB = memoryUsage / (1024 * 1024);

      if (memoryMB > this.MAX_MEMORY_MB * 0.8) {
        console.warn(`⚠️ High memory usage: ${memoryMB.toFixed(2)}MB`);
        this.evictLowPriorityCache();
      }

      if (process.env.NODE_ENV === "development") {
        console.log(
          `📊 Cache stats: ${this.cache.size} entries, ${memoryMB.toFixed(2)}MB`
        );
      }
    }, 120000); // Check every 2 minutes
  }

  // Enhanced request deduplication
  async deduplicateRequest(key, requestFn) {
    if (this.pendingRequests.has(key)) {
      console.log(`🔄 Deduplicating request: ${key}`);
      return this.pendingRequests.get(key);
    }

    const promise = requestFn().finally(() => {
      this.pendingRequests.delete(key);
    });

    this.pendingRequests.set(key, promise);
    return promise;
  }

  // Improved cache key generation
  generateCacheKey(method, url, params = null, data = null) {
    const baseKey = `${method.toUpperCase()}_${url}`;

    // Create a hash for complex parameters to avoid key collisions
    if (params || data) {
      const paramStr = JSON.stringify({ params, data });
      const hash = this.simpleHash(paramStr);
      return `${baseKey}_${hash}`;
    }

    return baseKey;
  }

  simpleHash(str) {
    let hash = 0;
    for (let i = 0; i < str.length; i++) {
      const char = str.charCodeAt(i);
      hash = (hash << 5) - hash + char;
      hash = hash & hash; // Convert to 32-bit integer
    }
    return hash.toString(36);
  }

  // Enhanced HTTP methods with circuit breaker
  async get(url, config = {}) {
    const { useCache = true, ...restConfig } = config;
    const cacheKey = this.generateCacheKey("GET", url, restConfig.params);

    // Check cache first for GET requests
    if (useCache) {
      const cached = this.getCachedResponse(cacheKey);
      if (cached) {
        return cached;
      }
    }

    // Use circuit breaker and deduplication
    return this.circuitBreaker.call(async () => {
      return this.deduplicateRequest(cacheKey, async () => {
        const response = await this.api.get(url, restConfig);

        // Cache successful GET responses
        if (useCache && response.status === 200) {
          this.setCachedResponse(cacheKey, response);
        }

        return response;
      });
    });
  }

  async post(url, data, config = {}) {
    return this.circuitBreaker.call(async () => {
      const response = await this.api.post(url, data, config);
      this.clearRelatedCache(url);
      return response;
    });
  }

  async put(url, data, config = {}) {
    return this.circuitBreaker.call(async () => {
      const response = await this.api.put(url, data, config);
      this.clearRelatedCache(url);
      return response;
    });
  }

  async delete(url, config = {}) {
    return this.circuitBreaker.call(async () => {
      const response = await this.api.delete(url, config);
      this.clearRelatedCache(url);
      return response;
    });
  }

  async patch(url, data, config = {}) {
    return this.circuitBreaker.call(async () => {
      const response = await this.api.patch(url, data, config);
      this.clearRelatedCache(url);
      return response;
    });
  }

  // Enhanced cache invalidation
  clearRelatedCache(url) {
    const urlPath = url.split("?")[0];
    const keysToDelete = [];

    for (const key of this.cache.keys()) {
      if (key.includes(urlPath)) {
        keysToDelete.push(key);
      }
    }

    keysToDelete.forEach((key) => {
      this.cache.delete(key);
      this.cacheTimestamps.delete(key);
      this.cachePriorities.delete(key);
    });

    if (keysToDelete.length > 0) {
      console.log(
        `🗑️ Cleared ${keysToDelete.length} cache entries related to ${urlPath}`
      );
    }
  }

  // Clear all cache
  clearCache() {
    this.cache.clear();
    this.cacheTimestamps.clear();
    this.cachePriorities.clear();
    this.pendingRequests.clear();
    console.log("🧹 All API cache cleared");
  }

  // Retry logic with exponential backoff
  shouldRetryRequest(error, originalRequest) {
    if (originalRequest.retryCount >= this.maxRetries) return false;

    const retryableStatuses = [408, 429, 500, 502, 503, 504];
    const isRetryable =
      retryableStatuses.includes(error.response?.status) || !error.response;

    return isRetryable;
  }

  async retryRequest(originalRequest) {
    const retryCount = originalRequest.retryCount || 0;
    const delay = this.retryDelay * Math.pow(2, retryCount); // Exponential backoff

    originalRequest.retryCount = retryCount + 1;

    console.log(
      `🔄 Retrying request (${retryCount + 1}/${
        this.maxRetries
      }) after ${delay}ms`
    );

    await this.delay(delay);
    return this.api(originalRequest);
  }

  // Utility methods
  delay(ms) {
    return new Promise((resolve) => setTimeout(resolve, ms));
  }

  generateRequestId() {
    return (
      "req_" + Date.now().toString(36) + Math.random().toString(36).substr(2, 5)
    );
  }

  sanitizeHeaders(headers) {
    const sanitized = { ...headers };
    if (sanitized.Authorization) {
      sanitized.Authorization = sanitized.Authorization.replace(
        /Bearer .+/,
        "Bearer [REDACTED]"
      );
    }
    return sanitized;
  }

  clearAuthData() {
    SecureStorage.removeItem("token");
    SecureStorage.removeItem("user");
    SecureStorage.removeItem("security_level");
    this.clearCache();
  }

  async refreshToken() {
    try {
      const response = await this.api.post(
        "/auth/refresh",
        {},
        { timeout: 10000 }
      );
      if (response.data.access_token) {
        SecureStorage.setItem("token", response.data.access_token);
        return true;
      }
      return false;
    } catch (error) {
      console.error("🔐 Token refresh failed:", error);
      return false;
    }
  }

  getFallbackResponse(url) {
    const fallbackEndpoints = {
      "/medical-professionals": { data: [] },
      "/exam-types": { data: [] },
      "/diagnoses": { data: [] },
      "/facilities": { data: [] },
    };

    for (const [endpoint, response] of Object.entries(fallbackEndpoints)) {
      if (url.includes(endpoint)) {
        return { data: response.data, status: 200 };
      }
    }

    return null;
  }

  // Get comprehensive stats
  getStats() {
    return {
      cache: {
        size: this.cache.size,
        memoryUsage: `${(this.getMemoryUsage() / (1024 * 1024)).toFixed(2)}MB`,
        maxSize: this.MAX_CACHE_SIZE,
        maxMemory: `${this.MAX_MEMORY_MB}MB`,
      },
      requests: {
        pending: this.pendingRequests.size,
        active: this.activeRequests,
        maxConcurrent: this.maxConcurrentRequests,
        queueLength: this.requestQueue.length,
      },
      circuitBreaker: this.circuitBreaker.getStats(),
      config: {
        timeout: this.timeout,
        maxRetries: this.maxRetries,
        retryDelay: this.retryDelay,
      },
    };
  }
}

// Create singleton instance
const apiService = new APIService();

// Create backward compatibility instance with same interceptors
const api = axios.create({
  baseURL: API_URL,
  timeout: 30000,
  headers: {
    "Content-Type": "application/json",
  },
});

// Apply same interceptors for backward compatibility
api.interceptors.request.use(apiService.api.interceptors.request);
api.interceptors.response.use(apiService.api.interceptors.response);

// Export the enhanced service
export { apiService };

// Export management functions
export const clearAPICache = () => apiService.clearCache();
export const getAPIStats = () => apiService.getStats();

// Export the backward compatibility instance as default
export default api;
